import { styled, css } from 'styled-components';

// ── Report detail / editor — outermost wrapper ──

export const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  z-index: 1;
  background: ${({ theme: { colors } }) => colors.brandLight};
`;

// ── Header bar (top row: back, title, actions) ──

export const HeaderBar = styled.div<{ $isEditing?: boolean }>`
  ${({ theme: { colors, spacings }, $isEditing }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s12};
    padding: ${spacings.s8} ${spacings.s24};
    background: ${$isEditing ? colors.brandPrimaryTint90 : colors.brandLight};
    border-bottom: 1px solid ${colors.brandDarkTint90};
    position: relative;
    z-index: 20;
    flex-shrink: 0;
  `}
`;

export const HeaderLeft = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s24};
    flex: 1;
    min-width: 0;

    input {
      margin: 0;
      border: none !important;
      box-shadow: none !important;
      padding-left: 0;
    }
  `}
`;

export const HeaderRight = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s8};
    flex-shrink: 0;
  `}
`;

export const EditingLabel = styled.span`
  ${({ theme: { colors, fontSizes } }) => css`
    font-size: ${fontSizes.s14};
    color: ${colors.brandPrimary};
    white-space: nowrap;
  `}
`;

export const BackButton = styled.button`
  ${({ theme: { colors, borderRadius } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: none;
    background: none;
    border-radius: ${borderRadius};
    cursor: pointer;
    color: ${colors.brandDarkAlpha50};
  `}
`;

export const TitleContainer = styled.div`
  label {
    background: transparent;
  }

  input {
    padding: 0.5em 0px 0.5em 1em;
  }

  button,
  button:disabled,
  input,
  input:focus,
  input:hover,
  label:focus {
    background: transparent;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    outline-color: transparent !important;
  }

  button svg,
  button i {
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.875rem;
  }
`;

// ── Header dropdown menu ──

export const HeaderDivider = styled.div`
  ${({ theme: { colors } }) => css`
    width: 1px;
    height: 1.5rem;
    background: ${colors.brandDarkTint80};
    flex-shrink: 0;
  `}
`;

export const HeaderMenuWrapper = styled.div`
  position: relative;

  @media print {
    display: none !important;
  }
`;

export const HeaderDropdownMenu = styled.div`
  ${({ theme: { borderRadius, colors } }) => css`
    position: absolute;
    top: calc(100% + 0.5rem);
    right: 0;
    z-index: 30;
    background: ${colors.brandLight};
    border-radius: ${borderRadius.sm};
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint80},
      0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    display: flex;
    flex-direction: column;
    min-width: 11rem;
    padding: 0.25rem;
  `}
`;

export const HeaderMenuItem = styled.button`
  ${({ theme: { borderRadius, colors, fontSizes, fontWeights, motionSpeeds, motions } }) => css`
    all: unset;
    align-items: center;
    border-radius: ${borderRadius.sm};
    cursor: pointer;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    outline: 2px solid transparent;
    padding: 0.75rem 1rem;
    transition: all ${motionSpeeds.fast} ${motions.default};

    p {
      color: ${colors.brandDark};
      flex: 1;
      font-size: ${fontSizes.s14};
      font-weight: ${fontWeights.medium};
      margin: 0;
      margin-right: 1rem;
    }

    span {
      color: ${colors.brandDarkTint30};
      font-size: ${fontSizes.s12};
    }

    svg {
      height: 1.125rem;
      width: 1.125rem;
    }

    &:hover {
      background: ${colors.brandPrimaryAlpha5};

      p {
        color: ${colors.brandPrimary};
      }

      svg path {
        stroke: ${colors.brandPrimary};
      }
    }

    &:focus-within,
    &:focus {
      outline-color: ${colors.brandPrimaryAlpha50};
      outline-offset: 2px;
    }
  `}
`;

// ── Toolbar row (accounts, date range) ──

export const MenuDivider = styled.div`
  ${({ theme: { colors } }) => css`
    height: 1px;
    background: ${colors.brandDarkTint90};
    margin: 0.25rem 0;
  `}
`;

// ── Toolbar row (accounts, date range) ──

export const Toolbar = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s8};
    padding: ${spacings.s12} ${spacings.s24};
    background: ${colors.brandLight};
    border-bottom: 1px solid ${colors.brandDarkTint90};
    flex-shrink: 0;
    overflow: visible;
    position: relative;
    z-index: 15;
  `}
`;

export const ToolbarLeft = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s12};
    flex: 1;
    min-width: 0;
  `}
`;

export const ToolbarRight = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s8};
    flex-shrink: 0;
    margin-left: auto;
  `}
`;

// ── Main content area: sidebar + module grid ──

export const ContentArea = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
    padding: ${spacings.s16} ${spacings.s24};
    background: ${colors.brandDarkTint95};
  `}
`;

// ── Sidebar wrapper with visibility control ──

export const SidebarWrapper = styled.div<{ $isVisible: boolean }>`
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  flex-direction: column;
`;

// ── Module grid ──

export const ModuleGridArea = styled.div`
  ${() => css`
    flex: 1;
    overflow: hidden;
    overflow-y: auto;
    box-sizing: border-box;
    width: 100%;
  `}
`;

export const ModuleGridInner = styled.div`
  ${({ theme: { spacings } }) => css`
    box-sizing: border-box;
    width: calc(100% - ${spacings.s8} - ${spacings.s8});
    margin-left: ${spacings.s8};
    margin-right: ${spacings.s8};
  `}
`;

export const ModuleGridContainer = styled.div<{ $isDragging?: boolean }>`
  ${({ theme: { colors }, $isDragging }) => css`
    /* Grid layout handled by react-grid-layout — this is just a wrapper */
    position: relative;

    .react-grid-layout {
      position: relative;
    }

    .react-grid-item {
      transition: transform 0.15s ease;
    }

    .react-grid-item.react-draggable-dragging,
    .react-grid-item.resizing {
      transition: none;
      z-index: 3;
      will-change: transform;
    }

    .react-grid-item.react-grid-placeholder {
      background: ${$isDragging ? 'transparent' : colors.brandPrimaryTint90};
      border: ${$isDragging ? 'none' : `1px dashed ${colors.brandPrimary}`};
      border-radius: 0.5rem;
      opacity: ${$isDragging ? 0 : 1};
    }

    .react-grid-item > .react-resizable-handle {
      position: absolute;
      width: 24px;
      height: 24px;
      bottom: 4px;
      right: 4px;
      cursor: se-resize;
      z-index: 2;
      all: unset;
      cursor: se-resize;
      position: absolute;
      width: 24px;
      height: 24px;
      bottom: 4px;
      right: 4px;
      z-index: 2;
    }

    .react-grid-item > .react-resizable-handle::after {
      content: none !important;
      display: none !important;
      border: none !important;
      border-right: none !important;
      border-bottom: none !important;
    }

    .react-grid-item[data-resizable='false'] > .react-resizable-handle {
      display: none !important;
      cursor: default !important;
    }
  `}
`;

// ── Empty report state ──

export const EmptyReportState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EmptyStateCard = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    background: ${colors.brandLight};
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacings.s12};
    padding: ${spacings.s12} ${spacings.s12} ${spacings.s20} ${spacings.s12};
    width: 23rem;
    margin-top: ${spacings.s80};
  `}
`;

export const EmptyStateInnerBox = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    box-sizing: border-box;
    width: 100%;
    background: ${colors.brandPrimaryTint90};
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${spacings.s24};
    margin-bottom: ${spacings.s12};

    div {
      border: 1px solid ${colors.brandPrimaryTint80};
      padding: 2rem;
      width: 100%;
      box-sizing: border-box;
      align-items: center;
      justify-content: center;
      display: flex;
      border-radius: 0.25rem;
      height: 7rem;
    }

    svg {
      width: 1.5rem;
      height: 1.5rem;
      color: ${colors.brandPrimary};
    }
  `}
`;

export const EmptyStateTitle = styled.p`
  ${({ theme: { fontSizes, fontWeights, colors } }) => css`
    font-size: ${fontSizes.s16};
    font-weight: ${fontWeights.semiBold};
    color: ${colors.brandDark};
    margin: 0;
    text-align: center;
    line-height: 1;
  `}
`;

export const EmptyStateSubtitle = styled.p`
  ${({ theme: { fontSizes, colors } }) => css`
    font-size: ${fontSizes.s12};
    color: ${colors.brandDarkTint20};
    margin: 0;
    text-align: center;
    line-height: 1;
    margin-bottom: 1rem;
  `}
`;

// ── Module grid item wrapper ──

export const ModuleGridItem = styled.div`
  height: 100%;
`;
